import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ClipboardCheck,
  Download,
  ExternalLink,
  Eye,
  FileWarning,
  Loader2,
  Plus,
  UserRound,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { farmGet, farmPost } from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

const FILTERS = [
  { id: "tutti", label: "Tutti" },
  { id: "in_struttura", label: "In struttura" },
  { id: "clienti_diretti", label: "Clienti diretti" },
  { id: "da_verificare", label: "Da verificare" },
  { id: "documenti_scadenza", label: "Documenti in scadenza" },
  { id: "terapie_aggiornate", label: "Terapie aggiornate" },
  { id: "carepy_in_ritardo", label: "Carepy in ritardo" },
];

const BAR_COLORS = ["#0F5C4C", "#2563eb", "#7c3aed", "#ea580c", "#dc2626", "#0891b2"];

function initials(nome = "", cognome = "") {
  const a = (cognome || "").trim()[0] || "";
  const b = (nome || "").trim()[0] || "";
  return `${a}${b}`.toUpperCase() || "?";
}

function formatShortDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso.includes("T") || iso.includes("-") ? iso : `${iso}T12:00:00`);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return String(iso).slice(0, 10);
  }
}

function relativeDayLabel(iso) {
  if (!iso) return null;
  try {
    const d = new Date(String(iso).slice(0, 10) + "T12:00:00");
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const diff = Math.round((d - today) / 86400000);
    if (diff === 0) return "Oggi";
    if (diff === 1) return "Domani";
    if (diff > 1 && diff <= 14) return `Tra ${diff} giorni`;
    return null;
  } catch {
    return null;
  }
}

function ageFromCfOrBirth(cf, dataNascita) {
  if (dataNascita) {
    try {
      const y = new Date(dataNascita).getFullYear();
      if (!Number.isNaN(y)) return new Date().getFullYear() - y;
    } catch {
      /* ignore */
    }
  }
  if (!cf || cf.length < 6) return null;
  try {
    let yy = parseInt(cf.slice(1, 3), 10);
    if (Number.isNaN(yy)) return null;
    const nowY = new Date().getFullYear() % 100;
    const century = yy <= nowY ? 2000 : 1900;
    return new Date().getFullYear() - (century + yy);
  } catch {
    return null;
  }
}

function KpiTile({ icon: Icon, label, value, tone }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    sky: "bg-sky-50 text-sky-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    violet: "bg-violet-50 text-violet-700",
  };
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-slate-500">{label}</p>
          <p className="mt-0.5 text-xl font-semibold tracking-tight text-slate-900">{value}</p>
        </div>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tones[tone] || tones.slate}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

function terapiaBadge(row) {
  if (row.terapia_da_visionare) {
    return { label: "Terapia da aggiornare", className: "border-amber-200 bg-amber-50 text-amber-800" };
  }
  if (row.tipologia === "cliente_diretto") {
    const n = (row.terapie || []).length || row.terapie_count || 0;
    if (n > 0) return { label: "Attiva", className: "border-emerald-200 bg-emerald-50 text-emerald-700" };
    return { label: "Da verificare", className: "border-slate-200 bg-slate-50 text-slate-600" };
  }
  if ((row.terapie_count || 0) > 0) {
    return { label: "Attiva", className: "border-emerald-200 bg-emerald-50 text-emerald-700" };
  }
  return { label: "Da verificare", className: "border-slate-200 bg-slate-50 text-slate-600" };
}

function documentiLabel(row) {
  const anoms = row.anomalie_documentali || [];
  if (anoms.some((a) => String(a).includes("delega") || String(a).includes("privacy"))) {
    return { text: "Privacy / delega mancante", tone: "text-rose-600" };
  }
  if (row.doc_in_scadenza) {
    return { text: "Documenti in scadenza", tone: "text-rose-600" };
  }
  if (anoms.length > 0) {
    return { text: `${anoms.length} anomalie documentali`, tone: "text-amber-700" };
  }
  if ((row.ricette_mancanti || 0) > 0 || (!row.ricette_medico_ricevute && row.tipologia === "in_struttura" && (row.carepy_count || 0) > 0)) {
    const n = row.ricette_mancanti || 1;
    return { text: `Ricette ${n} mancanti`, tone: "text-rose-600" };
  }
  return { text: "Documenti completi", tone: "text-emerald-700" };
}

function OspiteDetailPanel({ row, detail, loadingDetail, onClose, onConfirmTerapia }) {
  if (!row) {
    return (
      <aside className="hidden w-[320px] shrink-0 xl:block">
        <div className="sticky top-0 rounded-2xl border border-dashed border-slate-200 bg-white/60 p-6 text-center">
          <UserRound className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Assistito selezionato</p>
          <p className="mt-1 text-xs text-slate-400">Seleziona una riga dall&apos;elenco per il dettaglio.</p>
        </div>
      </aside>
    );
  }

  const nome = `${row.cognome || ""} ${row.nome || ""}`.trim() || "Assistito";
  const isCliente = row.tipologia === "cliente_diretto";
  const age = ageFromCfOrBirth(row.codice_fiscale, row.data_nascita || detail?.anagrafica?.data_nascita);
  const schedaTo = isCliente
    ? `/farmacia/ospite/${encodeURIComponent(row.cliente_id)}?tipo=cliente`
    : `/farmacia/ospite/${encodeURIComponent(row.fascicolo_id)}`;
  const tb = terapiaBadge(row);
  const docs = row.anomalie_documentali || [];
  const medico =
    detail?.medico_curante ||
    row.medico ||
    row.medico_nome ||
    "—";

  return (
    <aside className="w-full shrink-0 xl:w-[320px]">
      <div className="sticky top-0 flex max-h-[calc(100vh-10rem)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#0F5C4C] text-sm font-semibold text-white">
              {initials(row.nome, row.cognome)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <h3 className="truncate text-sm font-semibold text-slate-900">{nome}</h3>
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-slate-600 xl:hidden"
                  onClick={onClose}
                >
                  Chiudi
                </button>
              </div>
              <Badge
                variant="outline"
                className={`mt-1.5 text-[10px] ${
                  isCliente
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-sky-200 bg-sky-50 text-sky-700"
                }`}
              >
                {isCliente ? "Cliente diretto" : "In struttura"}
              </Badge>
              <p className="mt-2 space-y-0.5 text-xs text-slate-500">
                {age != null ? <span className="block">{age} anni</span> : null}
                <span className="block">Medico: {medico || "—"}</span>
                <span className="block truncate">
                  {isCliente ? "Canale: Consegna a domicilio" : `Struttura: ${row.struttura_nome || row.struttura || "—"}`}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 overflow-y-auto p-4">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Stato terapia
              </p>
              <Badge variant="outline" className={`text-[10px] ${tb.className}`}>
                {tb.label}
              </Badge>
            </div>
            {loadingDetail ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
            ) : (
              <ul className="space-y-1.5 text-xs">
                <li className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-2">
                  <span className="text-slate-600">Carepy</span>
                  <span className="font-medium text-slate-800">
                    {row.ciclo_carepy_stato === "in_ritardo"
                      ? "In ritardo"
                      : row.prossimo_carepy_due_at
                        ? `Atteso ${formatShortDate(row.prossimo_carepy_due_at)}`
                        : (row.carepy_count || 0) > 0 ? "Caricato" : "Nessuna attesa"}
                  </span>
                </li>
                <li className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-2">
                  <span className="text-slate-600">Ricette</span>
                  <span className="font-medium text-slate-800">
                    {(row.ricette_count || 0) > 0 ? `${row.ricette_count}` : "Mancanti"}
                  </span>
                </li>
                <li className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-2">
                  <span className="text-slate-600">Documenti</span>
                  <span className="font-medium text-slate-800">
                    {docs.length > 0 ? `${docs.length} da verificare` : "Ok"}
                  </span>
                </li>
              </ul>
            )}
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Documenti assistito
            </p>
            <ul className="space-y-1.5">
              {docs.length === 0 ? (
                <li className="rounded-lg border border-slate-100 px-2.5 py-2 text-xs text-slate-500">
                  Nessuna anomalia documentale
                </li>
              ) : (
                docs.map((a) => (
                  <li
                    key={a}
                    className="flex items-center justify-between gap-2 rounded-lg border border-rose-100 bg-rose-50/40 px-2.5 py-2 text-xs"
                  >
                    <span className="truncate text-slate-700">{String(a).replace(/_/g, " ")}</span>
                    <span className="shrink-0 font-medium text-rose-600">In attesa</span>
                  </li>
                ))
              )}
            </ul>
          </div>
          {row.tipologia === "in_struttura" && row.terapia_da_visionare ? (
            <Button size="sm" className="w-full bg-[#0F5C4C] hover:bg-[#0c4a3e]" onClick={() => onConfirmTerapia?.(row.fascicolo_id)}>
              <Eye className="mr-1.5 h-3.5 w-3.5" /> Conferma presa visione terapia
            </Button>
          ) : null}

          {(row.ordini_aperti || 0) > 0 ? (
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Ordini extra
              </p>
              <div className="rounded-lg border border-slate-100 px-2.5 py-2 text-xs text-slate-600">
                {row.ordini_aperti} attivi
              </div>
            </div>
          ) : null}

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Cronologia attività
            </p>
            <ol className="relative space-y-3 border-l border-slate-200 pl-3">
              {row.ultima_attivita ? (
                <li className="text-xs text-slate-600">
                  <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-[#0F5C4C]" />
                  Ultima attività
                  <span className="mt-0.5 block text-[10px] text-slate-400">
                    {formatShortDate(row.ultima_attivita)}
                  </span>
                </li>
              ) : null}
              {(row.carepy_count || 0) > 0 ? (
                <li className="text-xs text-slate-600">
                  <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-sky-400" />
                  Carepy presente ({row.carepy_count})
                </li>
              ) : null}
              {row.terapia_da_visionare ? (
                <li className="text-xs text-slate-600">
                  <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-amber-400" />
                  Terapia da visionare / aggiornare
                </li>
              ) : null}
              {!row.ultima_attivita && !(row.carepy_count > 0) && !row.terapia_da_visionare ? (
                <li className="text-xs text-slate-400">Nessuna attività recente</li>
              ) : null}
            </ol>
          </div>
        </div>

        <div className="mt-auto border-t border-slate-100 p-3">
          <Button asChild className="h-10 w-full rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]">
            <Link to={schedaTo}>
              Vai alla scheda assistito
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </aside>
  );
}

export default function FarmaciaOspiti() {
  const { token } = useFarmaciaAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [strutture, setStrutture] = useState([]);
  const [kpi, setKpi] = useState({});
  const [consegne, setConsegne] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(() => searchParams.get("filtro") || "tutti");
  const [orgFilter, setOrgFilter] = useState("all");
  const [selectedKey, setSelectedKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ nome: "", cognome: "", codice_fiscale: "", telefono: "", email: "", organization_id: "" });

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const [ospRes, cliRes, strRes, kpiRes, consRes] = await Promise.all([
        farmGet("/farmacia/ospiti", token).catch(() => ({ items: [] })),
        farmGet("/farmacia/clienti", token).catch(() => ({ items: [] })),
        farmGet("/farmacia/strutture", token, { lite: true }).catch(() => ({ items: [] })),
        farmGet("/farmacia/ricette/kpi", token).catch(() => ({})),
        farmGet("/farmacia/consegne", token, { data: today }).catch(() => ({ items: [] })),
      ]);

      const struttureItems = strRes.items || [];
      setStrutture(struttureItems);
      setKpi(kpiRes || {});
      setConsegne(consRes.items || consRes.consegne || []);

      const ospitiRows = (ospRes.items || []).map((o) => {
        const uniqAnoms = [...new Set((o.anomalie_documentali || []).map(String))];
        return {
          key: `s:${o.fascicolo_id}`,
          tipologia: "in_struttura",
          fascicolo_id: o.fascicolo_id,
          organization_id: o.organization_id,
          nome: o.nome,
          cognome: o.cognome,
          codice_fiscale: o.codice_fiscale,
          struttura_nome: o.struttura_nome || o.struttura,
          struttura: o.struttura,
          carepy_count: o.carepy_count ?? 0,
          ciclo_carepy_stato: o.ciclo_carepy_stato || null,
          prossimo_carepy_due_at: o.prossimo_carepy_due_at || null,
          ricette_count: o.ricette_count ?? 0,
          ordini_aperti: o.ordini_aperti ?? 0,
          terapie_count: o.terapie_count ?? 0,
          terapia_da_visionare: !!o.terapia_da_visionare,
          ricette_medico_ricevute: !!o.ricette_medico_ricevute,
          anomalie_documentali: uniqAnoms,
          doc_in_scadenza: uniqAnoms.length > 0,
          medico: o.medico,
          ultima_attivita: o.ultima_attivita,
        };
      });

      const clientiRows = (cliRes.items || []).map((c) => ({
        key: `c:${c.id}`,
        tipologia: "cliente_diretto",
        cliente_id: c.id,
        nome: c.nome,
        cognome: c.cognome,
        codice_fiscale: c.codice_fiscale,
        data_nascita: c.data_nascita,
        telefono: c.telefono,
        email: c.email,
        medico_id: c.medico_id,
        struttura_nome: "Consegna a domicilio",
        terapie: c.terapie || [],
        terapie_count: (c.terapie || []).length,
        terapia_da_visionare: false,
        anomalie_documentali: [],
        doc_in_scadenza: false,
        carepy_count: 0,
        ricette_count: 0,
        ordini_aperti: 0,
        ultima_attivita: c.updated_at || c.created_at,
      }));

      const merged = [...ospitiRows, ...clientiRows].sort((a, b) =>
        `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`, "it")
      );
      setRows(merged);
      setSelectedKey((prev) => {
        if (prev && merged.some((r) => r.key === prev)) return prev;
        return merged[0]?.key || null;
      });
    } catch {
      setRows([]);
      toast.error("Impossibile caricare gli assistiti");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const filtro = searchParams.get("filtro");
    if (FILTERS.some((item) => item.id === filtro)) setFilter(filtro);
  }, [searchParams]);

  const selected = rows.find((r) => r.key === selectedKey) || null;

  const confirmTerapia = async (fascicoloId) => {
    try {
      await farmPost(`/farmacia/ospiti/${encodeURIComponent(fascicoloId)}/terapia/presa-visione`, token);
      setRows((prev) => prev.map((row) => row.fascicolo_id === fascicoloId ? { ...row, terapia_da_visionare: false } : row));
      setDetail((prev) => prev ? { ...prev, presa_visione: { ...(prev.presa_visione || {}), da_visionare: false, stato: "visionata" } } : prev);
      toast.success("Terapia visionata");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Conferma presa visione non riuscita");
    }
  };

  useEffect(() => {
    if (!token || !selected || selected.tipologia !== "in_struttura" || !selected.fascicolo_id) {
      setDetail(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingDetail(true);
    farmGet(`/farmacia/ospiti/${encodeURIComponent(selected.fascicolo_id)}`, token)
      .then((res) => {
        if (!cancelled) setDetail(res);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, selected]);

  const consegnaByCf = useMemo(() => {
    // consegne sono per struttura, non per ospite — mostriamo prossima consegna struttura
    const byOrg = {};
    for (const c of consegne) {
      const oid = c.organization_id || c.organizationId;
      if (!oid) continue;
      if (!byOrg[oid] || String(c.data) < String(byOrg[oid].data)) byOrg[oid] = c;
    }
    return byOrg;
  }, [consegne]);

  const filtered = useMemo(() => {
    let list = rows;
    if (orgFilter !== "all") {
      list = list.filter((r) => r.organization_id === orgFilter);
    }
    if (filter === "in_struttura") list = list.filter((r) => r.tipologia === "in_struttura");
    if (filter === "clienti_diretti") list = list.filter((r) => r.tipologia === "cliente_diretto");
    if (filter === "da_verificare") {
      list = list.filter(
        (r) =>
          r.terapia_da_visionare ||
          (r.anomalie_documentali || []).length > 0 ||
          (!(r.terapie_count > 0) && r.tipologia === "cliente_diretto")
      );
    }
    if (filter === "documenti_scadenza") {
      list = list.filter((r) => r.doc_in_scadenza || (r.anomalie_documentali || []).length > 0);
    }
    if (filter === "terapie_aggiornate") {
      list = list.filter((r) => r.terapia_da_visionare);
    }
    if (filter === "carepy_in_ritardo") {
      list = list.filter((r) => r.ciclo_carepy_stato === "in_ritardo");
    }
    return list;
  }, [rows, filter, orgFilter]);

  const kpiValues = useMemo(() => {
    const inStruttura = rows.filter((r) => r.tipologia === "in_struttura").length;
    const clienti = rows.filter((r) => r.tipologia === "cliente_diretto").length;
    return {
      totali: rows.length,
      inStruttura,
      clienti,
      terapie: kpi.terapie_da_visionare ?? rows.filter((r) => r.terapia_da_visionare).length,
      documenti: kpi.documenti_in_scadenza ?? rows.filter((r) => r.doc_in_scadenza).length,
      ricette: kpi.ricette_mancanti ?? 0,
      carepyRitardo: kpi.carepy_in_ritardo ?? rows.filter((r) => r.ciclo_carepy_stato === "in_ritardo").length,
    };
  }, [rows, kpi]);

  const distribuzione = useMemo(() => {
    const map = {};
    for (const r of rows) {
      const label =
        r.tipologia === "cliente_diretto"
          ? "Clienti diretti"
          : r.struttura_nome || r.struttura || "Struttura";
      map[label] = (map[label] || 0) + 1;
    }
    const entries = Object.entries(map)
      .map(([nome, n]) => ({ nome, n }))
      .sort((a, b) => b.n - a.n)
      .slice(0, 6);
    const max = Math.max(1, ...entries.map((e) => e.n));
    return entries.map((e, i) => ({
      ...e,
      pct: (e.n / max) * 100,
      color: BAR_COLORS[i % BAR_COLORS.length],
    }));
  }, [rows]);

  const scadenzeDocs = useMemo(() => {
    return rows
      .filter((r) => (r.anomalie_documentali || []).length > 0 || r.doc_in_scadenza)
      .slice(0, 6)
      .map((r) => ({
        key: r.key,
        nome: `${r.cognome} ${r.nome}`.trim(),
        label: (r.anomalie_documentali || [])[0]
          ? String(r.anomalie_documentali[0]).replace(/_/g, " ")
          : "Documento",
        badge: "In scadenza",
      }));
  }, [rows]);

  const terapieDaAggiornare = useMemo(() => {
    return rows
      .filter((r) => r.terapia_da_visionare)
      .slice(0, 6)
      .map((r) => ({
        key: r.key,
        nome: `${r.cognome} ${r.nome}`.trim(),
        data: r.ultima_attivita,
      }));
  }, [rows]);

  const exportCsv = () => {
    const header = ["Cognome", "Nome", "CF", "Tipologia", "Struttura/Canale", "Terapia", "Documenti"];
    const lines = filtered.map((r) => {
      const tb = terapiaBadge(r);
      const dl = documentiLabel(r);
      return [
        r.cognome || "",
        r.nome || "",
        r.codice_fiscale || "",
        r.tipologia === "cliente_diretto" ? "Cliente diretto" : "In struttura",
        r.struttura_nome || "",
        tb.label,
        dl.text,
      ];
    });
    const csv = [header, ...lines]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ospiti-farmacia-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Esportazione avviata");
  };

  const openScheda = (r) => {
    if (r.tipologia === "cliente_diretto") {
      navigate(`/farmacia/ospite/${encodeURIComponent(r.cliente_id)}?tipo=cliente`);
      return;
    }
    navigate(`/farmacia/ospite/${encodeURIComponent(r.fascicolo_id)}`);
  };

  const createPaziente = async () => {
    if (!newForm.nome.trim() || !newForm.cognome.trim()) {
      toast.error("Nome e cognome sono obbligatori");
      return;
    }
    try {
      const result = await farmPost("/farmacia/ospiti", token, {
        ...newForm,
        organization_id: newForm.organization_id || null,
      });
      toast.success(result.tipo === "ospite" ? "Ospite creato e aderente alla struttura" : "Cliente diretto creato");
      setNewOpen(false);
      setNewForm({ nome: "", cognome: "", codice_fiscale: "", telefono: "", email: "", organization_id: "" });
      await load();
      navigate(`/farmacia/ospite/${encodeURIComponent(result.id)}${result.tipo === "cliente" ? "?tipo=cliente" : ""}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Creazione assistito fallita");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
      <div className="min-w-0 flex-1 space-y-5">
        <FarmaciaPageChrome>
          <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={exportCsv}>
            <Download className="mr-1.5 h-4 w-4" />
            Esporta
          </Button>
          <Button
            type="button"
            size="sm"
            className="rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]"
            onClick={() => setNewOpen((value) => !value)}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Nuovo assistito
          </Button>
        </FarmaciaPageChrome>

        {newOpen ? (
          <section className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4">
            <h2 className="text-sm font-semibold text-slate-800">Nuovo assistito</h2>
            <p className="mt-1 text-xs text-slate-500">Seleziona una struttura per creare l&apos;ospite aderente; lascia vuoto per un cliente diretto.</p>
            <div className="mt-3 grid gap-2 md:grid-cols-3">
              <Input placeholder="Nome *" value={newForm.nome} onChange={(e) => setNewForm({ ...newForm, nome: e.target.value })} />
              <Input placeholder="Cognome *" value={newForm.cognome} onChange={(e) => setNewForm({ ...newForm, cognome: e.target.value })} />
              <Input placeholder="Codice fiscale" value={newForm.codice_fiscale} onChange={(e) => setNewForm({ ...newForm, codice_fiscale: e.target.value })} />
              <Input placeholder="Telefono" value={newForm.telefono} onChange={(e) => setNewForm({ ...newForm, telefono: e.target.value })} />
              <Input placeholder="Email" value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} />
              <select className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm" value={newForm.organization_id} onChange={(e) => setNewForm({ ...newForm, organization_id: e.target.value })}>
                <option value="">Cliente diretto (nessuna struttura)</option>
                {strutture.map((struttura) => <option key={struttura.organization_id} value={struttura.organization_id}>{struttura.nome || struttura.denominazione}</option>)}
              </select>
            </div>
            <div className="mt-3 flex gap-2"><Button size="sm" onClick={createPaziente}>Crea assistito</Button><Button size="sm" variant="outline" onClick={() => setNewOpen(false)}>Annulla</Button></div>
          </section>
        ) : null}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-7">
          <KpiTile icon={Users} label="Assistiti totali" value={kpiValues.totali} tone="slate" />
          <KpiTile icon={UserRound} label="In struttura" value={kpiValues.inStruttura} tone="sky" />
          <KpiTile icon={Users} label="Clienti diretti" value={kpiValues.clienti} tone="emerald" />
          <KpiTile icon={Eye} label="Terapie da visionare" value={kpiValues.terapie} tone="amber" />
          <KpiTile icon={FileWarning} label="Documenti in scadenza" value={kpiValues.documenti} tone="rose" />
          <KpiTile icon={ClipboardCheck} label="Ricette da verificare" value={kpiValues.ricette} tone="violet" />
          <KpiTile icon={AlertTriangle} label="Carepy in ritardo" value={kpiValues.carepyRitardo} tone="rose" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex max-w-full flex-wrap gap-1.5 rounded-xl bg-slate-100/80 p-1">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition sm:text-xs ${
                  filter === f.id
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <select
            className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-xs text-slate-700"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
          >
            <option value="all">Tutte le strutture</option>
            {strutture.map((s) => (
              <option key={s.organization_id} value={s.organization_id}>
                {s.denominazione || s.nome}
              </option>
            ))}
          </select>
        </div>

        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="hidden border-b border-slate-100 bg-slate-50/80 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:grid lg:grid-cols-[minmax(0,1.4fr)_110px_minmax(0,1fr)_130px_minmax(0,1fr)_120px_100px] lg:gap-3">
            <span>Assistito</span>
            <span>Tipologia</span>
            <span>Struttura / Canale</span>
            <span>Stato terapia</span>
            <span>Stato documenti</span>
            <span>Prossima consegna</span>
            <span className="text-right">Azione</span>
          </div>
          {filtered.length === 0 ? (
            <div className="px-4 py-16 text-center">
              <Users className="mx-auto mb-3 h-8 w-8 text-slate-300" />
              <p className="text-sm font-medium text-slate-700">Nessun assistito</p>
              <p className="mt-1 text-xs text-slate-400">
                {rows.length === 0
                  ? "Gli ospiti aderenti e i clienti diretti compariranno qui."
                  : "Nessun risultato per i filtri selezionati."}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((r) => {
                const active = selectedKey === r.key;
                const tb = terapiaBadge(r);
                const dl = documentiLabel(r);
                const cons = r.organization_id ? consegnaByCf[r.organization_id] : null;
                const rel = relativeDayLabel(cons?.data);
                const isCliente = r.tipologia === "cliente_diretto";
                return (
                  <li key={r.key}>
                    <button
                      type="button"
                      onClick={() => setSelectedKey(r.key)}
                      className={`grid w-full gap-2 px-4 py-3 text-left transition hover:bg-slate-50/80 lg:grid-cols-[minmax(0,1.4fr)_110px_minmax(0,1fr)_130px_minmax(0,1fr)_120px_100px] lg:items-center lg:gap-3 ${
                        active ? "bg-emerald-50/40" : ""
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0F5C4C] text-[10px] font-semibold text-white">
                          {initials(r.nome, r.cognome)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">
                            {r.cognome} {r.nome}
                          </p>
                          {r.codice_fiscale ? (
                            <p className="truncate font-mono text-[10px] text-slate-400">{r.codice_fiscale}</p>
                          ) : null}
                          {r.ciclo_carepy_stato === "in_ritardo" ? (
                            <p className="mt-0.5 text-[10px] font-medium text-rose-700">Carepy atteso in ritardo</p>
                          ) : r.prossimo_carepy_due_at ? (
                            <p className="mt-0.5 text-[10px] text-slate-500">Carepy atteso: {formatShortDate(r.prossimo_carepy_due_at)}</p>
                          ) : null}
                        </div>
                      </div>
                      <div>
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            isCliente
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-sky-200 bg-sky-50 text-sky-700"
                          }`}
                        >
                          {isCliente ? "Cliente diretto" : "In struttura"}
                        </Badge>
                      </div>
                      <p className="truncate text-xs text-slate-600">{r.struttura_nome || "—"}</p>
                      <div>
                        <Badge variant="outline" className={`text-[10px] ${tb.className}`}>
                          {tb.label}
                        </Badge>
                      </div>
                      <p className={`truncate text-xs font-medium ${dl.tone}`}>{dl.text}</p>
                      <p className="text-xs text-slate-500">
                        {cons?.data ? (
                          <>
                            {rel || formatShortDate(cons.data)}
                            {cons.fascia_oraria ? `, ${cons.fascia_oraria}` : ""}
                          </>
                        ) : (
                          "—"
                        )}
                      </p>
                      <div className="text-right">
                        <span
                          role="link"
                          tabIndex={0}
                          className="inline-flex items-center gap-0.5 text-xs font-medium text-emerald-700 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openScheda(r);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              openScheda(r);
                            }
                          }}
                        >
                          Apri scheda
                          <ExternalLink className="h-3 w-3" />
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <div className="grid gap-3 lg:grid-cols-3">
          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Distribuzione assistiti</h3>
            {distribuzione.length === 0 ? (
              <p className="text-xs text-slate-400">Nessun dato</p>
            ) : (
              <ul className="space-y-2.5">
                {distribuzione.map((r) => (
                  <li key={r.nome}>
                    <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                      <span className="truncate text-slate-600">{r.nome}</span>
                      <span className="font-semibold tabular-nums text-slate-800">{r.n}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${r.pct}%`, backgroundColor: r.color }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Scadenze documenti</h3>
            {scadenzeDocs.length === 0 ? (
              <p className="text-xs text-slate-400">Nessuna scadenza documentale</p>
            ) : (
              <ul className="space-y-2">
                {scadenzeDocs.map((a) => (
                  <li
                    key={a.key}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-2 text-xs"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-800">{a.nome}</p>
                      <p className="truncate text-slate-400">{a.label}</p>
                    </div>
                    <Badge variant="outline" className="shrink-0 border-amber-200 text-[10px] text-amber-800">
                      {a.badge}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Terapie da aggiornare</h3>
            {terapieDaAggiornare.length === 0 ? (
              <p className="text-xs text-slate-400">Nessuna terapia da aggiornare</p>
            ) : (
              <ul className="space-y-2">
                {terapieDaAggiornare.map((t) => (
                  <li
                    key={t.key}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-2 text-xs"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                      <span className="truncate font-medium text-slate-800">{t.nome}</span>
                    </div>
                    <span className="shrink-0 text-slate-400">
                      {t.data ? formatShortDate(t.data) : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {selected ? (
          <div className="xl:hidden">
            <OspiteDetailPanel
              row={selected}
              detail={detail}
              loadingDetail={loadingDetail}
              onClose={() => setSelectedKey(null)}
              onConfirmTerapia={confirmTerapia}
            />
          </div>
        ) : null}
      </div>

      <div className="hidden xl:block">
        <OspiteDetailPanel
          row={selected}
          detail={detail}
          loadingDetail={loadingDetail}
          onClose={() => setSelectedKey(null)}
          onConfirmTerapia={confirmTerapia}
        />
      </div>
    </div>
  );
}
